import { parse } from 'querystring'

import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useCallback } from 'react'

import { DeprecatedText, Spacer } from '@syconium/little-miss-figgy'

import { useAuthentication } from '../../../../app/_providers/AuthenticationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { imageSrcs } from '../../../../constants/figs'
import { GrayHorizontalBar, PageWidthContainer } from '../../../../lib/shared-styles'
import { stringToBoolean } from '../../../../lib/utils'
import { ContentSection, ImageContentSplitContainer, ImageSection } from '../styles'

import { ForgotPassword } from './ForgotPassword'
import LoginForm from './LoginForm'
import { CreateAccountText, CreateAccountTextWrapper, PasswordReset, PointerSpan } from './styles'

const LogoutOrCreate: React.FC = () => {
  const { account, logOut } = useAuthentication()
  const {
    magnolia: {
      account: { login: strings },
    },
  } = useTranslation()
  const onLogOutClick = useCallback(() => {
    logOut()
  }, [logOut])

  return (
    <div>
      {account ? (
        <>
          <DeprecatedText>{`Not ${account.email}?`}</DeprecatedText>
          <DeprecatedText>
            <PointerSpan onClick={onLogOutClick}>{strings.clickHere}</PointerSpan>
          </DeprecatedText>
        </>
      ) : (
        <>
          <DeprecatedText textType='h2'>{strings.newToFigs}</DeprecatedText>
          <Spacer height={12} />
          <Link href='/account/register'>
            <CreateAccountTextWrapper>
              <CreateAccountText>{strings.createAnAccount}</CreateAccountText>
            </CreateAccountTextWrapper>
          </Link>
        </>
      )}
    </div>
  )
}

const Login: React.FC<{
  redirectUrl?: string
  checkoutUrl?: string
  displayPasswordReset: (arg0: boolean) => void
  isPasswordResetSuccessful?: boolean
}> = ({ checkoutUrl, displayPasswordReset, isPasswordResetSuccessful, redirectUrl }) => {
  const {
    magnolia: {
      account: { login: strings, reset: passwordResetStrings },
    },
  } = useTranslation()

  return (
    <>
      <DeprecatedText textType='h1' role='status' aria-live='polite'>
        {strings.welcomeBack}
      </DeprecatedText>
      <Spacer height={12} />
      <DeprecatedText asTag='h1'>{strings.welcomeBackToFigs}</DeprecatedText>
      <Spacer height={24} />
      {isPasswordResetSuccessful && (
        <PasswordReset state='success'>{passwordResetStrings.successMessage}</PasswordReset>
      )}
      <LoginForm {...{ checkoutUrl, displayPasswordReset, redirectUrl }} />
      <GrayHorizontalBar />
      <LogoutOrCreate />
      <Spacer height={75} />
    </>
  )
}

interface QueryParams {
  redirect_url?: string
  checkout_url?: string
  displayForgotPasswordScreen?: string
  isPasswordResetSuccessful?: string
}

export const LoginPageView = () => {
  const router = useRouter()
  const query: QueryParams = parse(router.asPath.substr(router.asPath.indexOf('?') + 1))
  const displayForgotPasswordScreen = stringToBoolean(query.displayForgotPasswordScreen) ?? false
  const isPasswordResetSuccessful = stringToBoolean(query.isPasswordResetSuccessful)
  const checkoutUrl = query.checkout_url
  const redirectUrl = query.redirect_url

  const [isResettingPassword, setIsResettingPassword] = React.useState<boolean>(
    displayForgotPasswordScreen
  )
  const [passwordResetSuccess, setPasswordResetSuccess] = React.useState<boolean>(false)
  const {
    magnolia: {
      account: { login: strings, reset: passwordResetStrings },
    },
  } = useTranslation()

  const displayPasswordReset = (display: boolean) => {
    setIsResettingPassword(display)
  }

  return (
    <>
      <PageWidthContainer>
        <ImageContentSplitContainer>
          <ImageSection>
            {/* TODO: Fix this auto-generated suppression. */}
            {/* eslint-disable-next-line @next/next/no-img-element, rulesdir/require-responsive-images */}
            <img src={imageSrcs.login} alt={strings.image.alt} />
          </ImageSection>
          <ContentSection>
            {passwordResetSuccess && (
              <PasswordReset state='success'>{strings.passwordResetSuccess}</PasswordReset>
            )}
            {isResettingPassword ? (
              <ForgotPassword
                {...{ displayPasswordReset }}
                passwordResetError={
                  isPasswordResetSuccessful === false
                    ? passwordResetStrings.errorMessage
                    : undefined
                }
                passwordResetSuccess={setPasswordResetSuccess}
              />
            ) : (
              <Login
                {...{ checkoutUrl, displayPasswordReset, isPasswordResetSuccessful, redirectUrl }}
              />
            )}
          </ContentSection>
        </ImageContentSplitContainer>
      </PageWidthContainer>
    </>
  )
}
